import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout/Layout';
// import Dashboard from './pages/Dashboard/Dashboard'; // возможно пригодится позже
import { prefetchRemotes } from './prefetchRemotes';
import { safeLazyImport } from './utils/safeLazyImport';
import { safeUseAuth } from './utils/safeUseAuth';
// Виджет ИИ-помощника выведен из эксплуатации вместе с /kanban — чат живёт в /arm.
// import { deriveChatWidgetContext } from './utils/chatWidgetContext';
import BankAccountsFallback from './pages/BankAccounts/BankAccountsPage';
import EmailFileExtractorFallback from './pages/EmailFileExtractor/EmailFileExtractorPage';
import { defaultRoute } from './utils/remotes';

// Стартовая страница инсталляции: у контура без витрин данных «Отчёты» пусты (utils/remotes.ts).
const DEFAULT_ROUTE = defaultRoute((import.meta as any).env || {});


// Динамический импорт компонентов auth микрофронтенда с безопасной обработкой ошибок
const LoginForm = safeLazyImport(() => import(/* @vite-ignore */ 'auth/LoginForm'), 'auth/LoginForm');
const RegisterForm = safeLazyImport(() => import(/* @vite-ignore */ 'auth/RegisterForm'), 'auth/RegisterForm');
const EmailVerificationForm = safeLazyImport(() => import(/* @vite-ignore */ 'auth/EmailVerificationForm'), 'auth/EmailVerificationForm');
const Notification = safeLazyImport(() => import(/* @vite-ignore */ 'auth/Notification'), 'auth/Notification');
const ProfilePage = safeLazyImport(() => import(/* @vite-ignore */ 'auth/ProfilePage'), 'auth/ProfilePage');
const OrgStructureApp = safeLazyImport(() => import(/* @vite-ignore */ 'orgStructure/OrgStructureApp'), 'orgStructure/OrgStructureApp');
const DocumentConstructorPage = React.lazy(() => import('./pages/DocumentConstructor'));
const DocumentsApp = safeLazyImport(() => import(/* @vite-ignore */ 'documents/DocumentsApp'), 'documents/DocumentsApp');
const TaxServiceApp = safeLazyImport(() => import(/* @vite-ignore */ 'taxService/TaxServiceApp'), 'taxService/TaxServiceApp');
const RulesPage = React.lazy(() => import('./pages/Rules'));
const IntegrationsPage = React.lazy(() => import('./pages/Integrations'));
const PayrollApp = safeLazyImport(() => import(/* @vite-ignore */ 'payroll/PayrollApp'), 'payroll/PayrollApp');
const IntInvoicesApp = safeLazyImport(() => import(/* @vite-ignore */ 'intInvoices/IntInvoicesApp'), 'intInvoices/IntInvoicesApp');
const TemplateSettingsPage = safeLazyImport(() => import(/* @vite-ignore */ 'documents/TemplateSettingsPage'), 'documents/TemplateSettingsPage');
const ReportsPage = React.lazy(() => import('./pages/Reports'));
const BankAccountsApp = safeLazyImport(
  () => import(/* @vite-ignore */ 'bankAccounts/BankAccountsApp'),
  'bankAccounts/BankAccountsApp',
  BankAccountsFallback
);
const AggregatorServiceApp = safeLazyImport(
  () => import(/* @vite-ignore */ 'aggregatorService/AggregatorServiceApp'),
  'aggregatorService/AggregatorServiceApp'
);
// Канбан выведен из эксплуатации: доска живёт в /arm.
// const KanbanApp = safeLazyImport(
//   () => import(/* @vite-ignore */ 'kanbanApp/KanbanApp'),
//   'kanbanApp/KanbanApp'
// );
const ArmApp = safeLazyImport(
  () => import(/* @vite-ignore */ 'armApp/ArmApp'),
  'armApp/ArmApp'
);
// Виджет ИИ-помощника выведен из эксплуатации вместе с сервисом
// osnova-settings-agent — чат встроен в /arm.
// const SilentFallback = () => null;
// const ChatWidgetApp = safeLazyImport(
//   () => import(/* @vite-ignore */ 'chatWidget/ChatWidgetApp'),
//   'chatWidget/ChatWidgetApp',
//   SilentFallback
// );
const FilesRecognitionPage = React.lazy(() => import('./pages/FilesRecognition'));
const DataSourcesPage = React.lazy(() => import('./pages/DataSources'));
const SupersetEmbedPage = React.lazy(() => import('./pages/SupersetEmbed'));
const EmailFileExtractorApp = safeLazyImport(
  () => import(/* @vite-ignore */ 'emailFileExtractor/EmailFileExtractorApp'),
  'emailFileExtractor/EmailFileExtractorApp',
  EmailFileExtractorFallback
);
// useAuth — это хук, его нельзя загружать через React.lazy. Если понадобится, импортируйте его обычным способом.

function App() {
  // Получаем данные авторизации из общего стора микрофронтенда auth
  // Используем safeUseAuth для безопасной загрузки модуля auth
  const { isLoading: authLoading, isUserAuthenticated, logout: authLogout } = safeUseAuth();
  const location = useLocation();

  // Обработка ошибок загрузки модулей через Error Boundary
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Игнорируем ошибки загрузки модулей, так как они обрабатываются через safeLazyImport
      if (event.message?.includes('Failed to fetch dynamically imported module') ||
          event.message?.includes('Loading chunk') ||
          event.filename?.includes('remoteEntry')) {
        console.warn('[App] Module load error handled by safeLazyImport:', event.message);
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Одноразовая миграция данных из старого формата в новый
  useEffect(() => {
    try {
      const oldOrgId = localStorage.getItem('selectedOrgId');
      const oldOrgName = localStorage.getItem('selectedOrgName');
      const newKey = localStorage.getItem('osnova_org_current_organization');
      
      if (oldOrgId && oldOrgName && !newKey) {
        // Мигрируем старые данные в новый формат
        const org = {
          id: parseInt(oldOrgId, 10),
          name: oldOrgName
        };
        localStorage.setItem('osnova_org_current_organization', JSON.stringify(org));
        
        // Удаляем старые ключи
        localStorage.removeItem('selectedOrgId');
        localStorage.removeItem('selectedOrgName');
        
        console.log('✅ [Migration] Migrated organization data to new format:', org);
      }
    } catch (error) {
      console.warn('⚠️ [Migration] Failed to migrate organization data:', error);
    }
  }, []);

  useEffect(() => {
    prefetchRemotes();
  }, []);

  // Используем функцию из хука, чтобы определить, авторизован ли пользователь
  const isAuthenticated = isUserAuthenticated();
  // const showChatWidget = isAuthenticated && (
  //   location.pathname === '/kanban' || location.pathname.startsWith('/kanban/')
  // );

  // Что открыто у пользователя: раздел канбана и, если он раскрыт, номер
  // карточки. Само правило вынесено в `utils/chatWidgetContext` — там же
  // объяснено, почему раздел отдаётся и без карточки.
  // const chatWidgetContext = React.useMemo(
  //   () => deriveChatWidgetContext(location.pathname, location.search, showChatWidget),
  //   [showChatWidget, location.pathname, location.search]
  // );

  const handleLogout = async () => {
    await authLogout();
  };

  // Функция-заглушка, если формы авторизации ожидают проп onSuccess
  const handleAuthSuccess = () => {
    /* noop – авторизация обрабатывается глобальным стором */
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Приводим Suspense к any, чтобы отключить проверку onError пропа в TS
  const SuspenseAny: any = Suspense;

  return (
    <SuspenseAny
      fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      }
      onError={(error: any) => {
        // Ошибки загрузки модулей обрабатываются через safeLazyImport
        // Здесь только логируем для отладки
        console.warn('[App] Module load error (handled by safeLazyImport):', error);
      }}
    >
      <Routes>
        <Route path="/login" element={
          !isAuthenticated ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '100vh',
              bgcolor: 'background.default'
            }}>
              <LoginForm onSuccess={handleAuthSuccess} />
            </Box>
          ) : (
            <Navigate to={DEFAULT_ROUTE} replace />
          )
        } />
        
        <Route path="/register" element={
          !isAuthenticated ? (
            <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh', bgcolor:'background.default' }}>
              <RegisterForm onSuccess={handleAuthSuccess} />
            </Box>
          ) : <Navigate to={DEFAULT_ROUTE} replace />
        } />
        <Route path="/verify-email" element={
          !isAuthenticated ? (
            <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh', bgcolor:'background.default' }}>
              <EmailVerificationForm onSuccess={handleAuthSuccess} />
            </Box>
          ) : <Navigate to={DEFAULT_ROUTE} replace />
        } />
        
        <Route path="/*" element={
          isAuthenticated ? (
            <Box sx={{ display: 'flex' }}>
              <Layout onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<Navigate to={DEFAULT_ROUTE} replace />} />

                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/org-chart/*" element={<OrgStructureApp />} />
                  <Route path="/reports/*" element={<ReportsPage />} />
                  <Route path="/tax/*" element={<TaxServiceApp />} />
                  <Route path="/rules/*" element={<RulesPage />} />
                  <Route path="/integrations/*" element={<IntegrationsPage />} />
                  <Route path="/payroll/*" element={<PayrollApp />} />
                  <Route path="/doc-constructor/*" element={<DocumentConstructorPage />} />
                  <Route path="/int-invoices/*" element={<IntInvoicesApp />} />
                  <Route path="/bank-accounts/*" element={<BankAccountsApp />} />
                  {/* Канбан выведен из эксплуатации: старые ссылки ведут в АРМ */}
                  <Route path="/kanban/*" element={<Navigate to="/arm" replace />} />
                  <Route path="/arm/*" element={<ArmApp />} />
                  <Route path="/data-sources/*" element={<DataSourcesPage />} />
                  <Route path="/aggregator-service/*" element={<AggregatorServiceApp />} />
                  <Route path="/files-recognition/*" element={<FilesRecognitionPage />} />
                  <Route path="/email-file-extractor/*" element={<EmailFileExtractorApp />} />
                  <Route path="/superset-embed" element={<SupersetEmbedPage />} />

                  <Route path="*" element={<Navigate to={DEFAULT_ROUTE} replace />} />
                </Routes>
              </Layout>
            </Box>
          ) : (
            <Navigate to="/login" replace />
          )
        } />
      </Routes>
      {/* Глобальные всплывающие уведомления из auth микрофронтенда */}
      <Notification />
      {/* {showChatWidget && (
        <Suspense fallback={null}>
          <ChatWidgetApp context={chatWidgetContext} />
        </Suspense>
      )} */}
    </SuspenseAny>
  );
}

export default App;
